import { useMemo } from 'react';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import {
    CreateForecastDTO,
    CustomerSegmentWithRelations,
    DayTimeSlot,
    ForecastDataDTO,
    ForecastDataGroupedByType,
    ForecastRunWithForecast,
    ForecastsCopyToOfferedParamsDTO,
    ForecastsCopyToOfferedResultDTO,
    ForecastWithRequiredRelations,
    HistoricVolumeByWorkingUnitUploadResult,
    HistoricVolumeBatchUploadResult,
} from '@callmart/types';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {
    createForecast,
    deleteForecast,
    deleteHistoricVolume,
    deleteHistoricVolumesByWorkingUnit,
    exportForecastResults,
    getDayTimeSlots,
    getForecast,
    getForecastHistoricData,
    getForecastPredictions,
    getForecastResultsForApi,
    getForecasts,
    getCustomerSegments,
    startForecast,
    uploadHistoricVolumeByWorkingUnit,
    uploadHistoricVolumeBatch,
    updateForecast,
    copyForecastsToOffered,
} from '../../../services/forecasts';
import { QueryKey, QueryKey as QueryKeys } from '../../../enums/QueryKey';
import { useDispatchErrorNotification } from '../../useDispatchErrorNotification';
import { useDispatchSuccessfullySavedRecord } from '../../useDispatchSuccessfullySavedRecord';
import { queryClient } from '../../../services/queryClient';
import {
    useDispatchSuccessfullyDeletedRecord,
    useDispatchSuccessNotification,
} from '../../useDispatchSuccessNotification';
import { useAlertMessage } from '../../useAlertMessage';
import { downloadBlobFile } from '../../../utils/fileUtils';

export function useForecast(
    id: number,
    { refetchInterval, runOffset }: { refetchInterval?: number; runOffset?: number | string | null } = {}
) {
    const { data, ...rest } = useQuery({
        queryKey: [QueryKey.Forecasts, id, runOffset],
        queryFn: () => getForecast(id, runOffset),
        enabled: !!id, // Only fetch if id is provided
        refetchOnWindowFocus: false,
        refetchInterval,
    });

    return { data, ...rest };
}

export function useForecasts({ refetchInterval }: { refetchInterval?: number } = {}) {
    const { data, ...rest } = useQuery<ForecastWithRequiredRelations[]>({
        queryKey: [QueryKey.Forecasts],
        queryFn: getForecasts,
        refetchOnWindowFocus: true,
        refetchInterval: refetchInterval, // Optional refetch interval in ms
    });

    return { data: data || [], ...rest };
}

export function useForecastHistoricData(forecastId: number, params: ForecastDataDTO) {
    const { data, ...rest } = useQuery<ForecastDataGroupedByType[]>({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- forecastId & params are embedded via .concat(); the rule can't see them statically
        queryKey: [QueryKey.HistoricData, forecastId].concat(
            Object.entries(params).map(([key, value]) => `${key}:${value}`)
        ),
        queryFn: () => getForecastHistoricData(forecastId, params),
        enabled: !!forecastId, // Only fetch if forecastId is provided
        refetchOnWindowFocus: false,
    });

    return { data: data || [], ...rest };
}

/**
 * Fetches per-day forecast sums across the given working units for the date range, using the
 * latest completed run per working unit. Returns ISO-date keys in user timezone.
 * Used by Adjustment dialog to auto-distribute absolute totals proportionally to forecast weight.
 */
export function useForecastWeights({
    workingUnitIds,
    startDate,
    endDate,
}: {
    workingUnitIds: number[];
    startDate?: string | null;
    endDate?: string | null;
}) {
    const sortedIds = useMemo(() => [...workingUnitIds].sort((a, b) => a - b), [workingUnitIds]);
    const enabled = !!startDate && !!endDate && sortedIds.length > 0;

    const queries = useQueries({
        queries: sortedIds.map((workingUnitId) => ({
            queryKey: [QueryKey.ForecastWeights, workingUnitId, startDate, endDate],
            queryFn: () =>
                getForecastResultsForApi({
                    workingUnitId,
                    dateFrom: startDate as string,
                    dateTo: endDate as string,
                }),
            enabled,
            refetchOnWindowFocus: false,
            staleTime: 60_000,
        })),
    });

    return useMemo(() => {
        const isLoading = queries.some((q) => q.isLoading);
        const isError = queries.some((q) => q.isError);
        const byDay: Record<string, number> = {};
        let anyData = false;

        for (const q of queries) {
            const payload = q.data;
            if (!payload) continue;
            for (const row of payload.data || []) {
                // Use backend-localized date (DD.MM.YYYY, computed via AT TIME ZONE :timezone).
                // Parsing row.dateTime here would re-bucket half-hour slots by browser timezone.
                const iso = row.date.split('.').reverse().join('-');
                byDay[iso] = (byDay[iso] || 0) + Number(row.value || 0);
                anyData = true;
            }
        }

        return { isLoading, isError, data: byDay, hasData: anyData };
    }, [queries]);
}

export function useForecastPredictions(runId: number, params: ForecastDataDTO) {
    const { data, ...rest } = useQuery<ForecastDataGroupedByType[]>({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps -- runId & params are embedded via .concat(); the rule can't see them statically
        queryKey: [QueryKey.Predictions, runId].concat(Object.entries(params).map(([key, value]) => `${key}:${value}`)),
        queryFn: () => getForecastPredictions(runId, params),
        enabled: !!runId, // Only fetch if runId is provided
        refetchOnWindowFocus: false,
    });

    return { data: data || [], ...rest };
}

export function useCreateForecast() {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<ForecastWithRequiredRelations, Error, CreateForecastDTO>({
        mutationFn: createForecast,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Forecasts]);
            dispatchSuccessfullySavedRecord('Forecast');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

export function useUpdateForecast(forecastId: number) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<ForecastWithRequiredRelations, Error, { id: number; data: CreateForecastDTO }>({
        mutationFn: ({ id, data }) => updateForecast(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Forecasts]);
            queryClient.invalidateQueries([QueryKey.Forecasts, forecastId]);
            dispatchSuccessfullySavedRecord('Forecast');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

export function useStartForecast() {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullySavedRecord } = useDispatchSuccessfullySavedRecord();

    return useMutation<ForecastRunWithForecast, Error, number>({
        mutationFn: startForecast,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Forecasts]);
            dispatchSuccessfullySavedRecord('Forecast started');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

export function useDeleteForecast() {
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullyDeletedRecord } = useDispatchSuccessfullyDeletedRecord();

    return useMutation<{ success: boolean }, AxiosError, number>({
        mutationFn: deleteForecast,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKey.Forecasts]);
            dispatchSuccessfullyDeletedRecord('Forecast');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

export const useDeleteHistoricVolume = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullyDeletedRecord } = useDispatchSuccessfullyDeletedRecord();

    return useMutation<{ success: boolean; message: string }, AxiosError, number>({
        mutationFn: deleteHistoricVolume,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.HistoricVolumes]);
            queryClient.invalidateQueries([QueryKey.WorkingUnits]);
            dispatchSuccessfullyDeletedRecord('Historic volume file');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message || t('error.deleteHistoricFileFailed') });
        },
    });
};

export const useDeleteHistoricVolumesByWorkingUnit = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessfullyDeletedRecord } = useDispatchSuccessfullyDeletedRecord();

    return useMutation<{ success: boolean; message: string }, AxiosError, number>({
        mutationFn: deleteHistoricVolumesByWorkingUnit,
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.HistoricVolumes]);
            queryClient.invalidateQueries([QueryKey.WorkingUnits]);
            dispatchSuccessfullyDeletedRecord('Historic volume data');
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message || t('error.deleteHistoricDataFailed') });
        },
    });
};

export const useDayTimeSlots = () => {
    const { data, ...rest } = useQuery<DayTimeSlot[]>({
        queryKey: [QueryKeys.DayTimeSlots],
        queryFn: getDayTimeSlots,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: false,
    });

    return { data: data || [], ...rest };
};

export const useCustomerSegments = () => {
    const { data, ...rest } = useQuery<CustomerSegmentWithRelations[]>({
        queryKey: [QueryKeys.CustomerSegments],
        queryFn: getCustomerSegments,
        refetchOnWindowFocus: false,
    });

    return { data: data || [], ...rest };
};

export const useExportForecastResults = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    return useMutation<{ data: Blob; contentType: string; fileName: string }, AxiosError, number>({
        mutationFn: exportForecastResults,
        onSuccess: ({ data, fileName, contentType }) => {
            downloadBlobFile({ data, fileName, contentType });
            dispatchSuccessNotification({ message: t('notification.resultsExported') });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message || t('error.exportResultsFailed') });
        },
    });
};

export const useCopyForecastsToOffered = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<ForecastsCopyToOfferedResultDTO, AxiosError, ForecastsCopyToOfferedParamsDTO>({
        mutationFn: copyForecastsToOffered,

        onError(error) {
            const message = t('error.copyToOfferedFailed', { message: error.message ? `: ${error.message}` : '' });

            dispatchErrorNotification({ message });
        },
    });
};

export const useUploadHistoricVolumeBatch = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();
    const { dispatchAlert } = useAlertMessage();

    return useMutation<HistoricVolumeBatchUploadResult, AxiosError, File>({
        mutationFn: uploadHistoricVolumeBatch,
        onSuccess: (data) => {
            queryClient.invalidateQueries([QueryKeys.HistoricVolumes]);
            queryClient.invalidateQueries([QueryKey.WorkingUnits]);
            const successCount = data.results.filter((r) => !r.unchanged).length;
            const unchangedCount = data.results.filter((r) => r.unchanged).length;
            const hasIssues = data.unmatchedRows.length > 0 || data.unmatchedWorkingUnits.length > 0;
            const parts: string[] = [];
            if (successCount > 0) {
                parts.push(t('import.workingUnitsUpdated', { count: successCount }));
            }
            if (unchangedCount > 0) {
                parts.push(t('import.unchanged', { count: unchangedCount }));
            }
            if (data.unmatchedWorkingUnits.length > 0) {
                parts.push(t('import.unmatchedUnits', { count: data.unmatchedWorkingUnits.length }));
            }
            if (data.unmatchedRows.length > 0) {
                parts.push(t('import.unmatchedRows', { count: data.unmatchedRows.length }));
            }
            const message = parts.length > 0 ? parts.join(', ') : t('upload.complete');
            if (hasIssues) {
                dispatchAlert({ message, type: 'warning' });
            } else {
                dispatchSuccessNotification({ message });
            }
        },
        onError: (error) => {
            dispatchErrorNotification({
                message: error.message || t('error.uploadHistoricFailed'),
            });
        },
    });
};

export const useUploadHistoricVolumeByWorkingUnit = () => {
    const { t } = useTranslation('forecasts');
    const { dispatchErrorNotification } = useDispatchErrorNotification();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    return useMutation<HistoricVolumeByWorkingUnitUploadResult, AxiosError, { file: File; workingUnitId: number }>({
        mutationFn: ({ file, workingUnitId }) => uploadHistoricVolumeByWorkingUnit(file, workingUnitId),
        onSuccess: (data) => {
            queryClient.invalidateQueries([QueryKeys.HistoricVolumes]);
            queryClient.invalidateQueries([QueryKey.WorkingUnits]);
            const message = data.unchanged
                ? t('upload.unchanged')
                : data.softDeleted > 0
                  ? t('upload.successCreatedReplaced', {
                        created: data.created,
                        softDeleted: data.softDeleted,
                    })
                  : t('upload.successCreated', {
                        created: data.created,
                    });
            dispatchSuccessNotification({ message });
        },
        onError: (error) => {
            dispatchErrorNotification({
                message: error.message || t('error.uploadHistoricFailed'),
            });
        },
    });
};

export * from './customerMetrics';
